<template>
  <div class="content">
    <div v-if="readonly && !hasDocuments" class="card card--info">
      <img class="card__icon icon icon--primary" src="~images/info.svg" v-svg-inline/>
      <p>К заявлению пока не прикреплены документы. Когда файлы появятся в заявлении, они отобразятся в этом разделе.</p>
    </div>

    <template v-else>
      <template v-if="documentFiles.length">
        <h2>Документы, подготовленные ВУЗом</h2>

        <div class="card card--info" v-if="store.application.fdo">
          <img class="card__icon icon icon--primary" src="~images/info.svg" v-svg-inline/>
          <p>
            Скачайте и проверьте бланки документов, сформированные в данном разделе.<br>
            Распечатайте и поставьте подписи. Воспользуйтесь подробной
            <strong>
              <a href="https://storage.tusur.ru/files/190467/2026-doc-fdo.pdf" target="_blank">инструкцией</a>
            </strong>
            по формированию и отправке пакета документов!
          </p>
        </div>

        <div
          v-for="document in documentFiles"
          :key="document.id || document.file.path"
          style="display: flex; flex-direction: column; gap: 16px;"
        >
          <div style="display: flex; gap: 8px; align-items:center;">
            <div class="scan-preview__slot">
              <div class="scan-preview__block">
                <img
                  v-if="isImage(document.file.filename)"
                  :src="document.file.path"
                  class="scan-preview__img"
                  @click="toggleBlock($event)"
                />
                <div v-else class="scan-preview__pdf">PDF</div>

                <div class="scan-preview__actions">
                  <a :href="document.file.path" download class="scan-preview__icon scan-preview__icon--download" @click.stop>
                    <span>Скачать</span>
                    <img src="@images/download.svg" v-svg-inline />
                  </a>
                </div>
              </div>
            </div>
            <div class="scan-preview__info">
              <span>{{ document.category }}</span>
              <span v-if="document.created_at" class="scan-preview__date">Загружен: {{ document.created_at }}</span>
            </div>
          </div>
        </div>
      </template>

      <template v-if="visibleCategories.length">
        <h2>Дополнительные документы (по запросу)</h2>

        <div v-for="category in visibleCategories" :key="category.value" class="join join--col">
          <div class="scan-category__header">
            <h3>{{ category.text }}</h3>
            <button
              v-if="canUploadCategory(category.value)"
              type="button"
              class="scan-category__toggle"
              :class="{ 'scan-category__toggle--active': isExpanded(category.value) }"
              @click="toggleCategory(category.value)"
            >
              <img src="@images/plus.svg" v-svg-inline />
              <span>{{ isExpanded(category.value) ? 'Скрыть' : 'Добавить' }}</span>
            </button>
          </div>

          <div v-if="categoryBannerText(category.value)" class="card card--info">
            <img class="card__icon icon icon--primary" src="~images/info.svg" v-svg-inline/>
            <p>{{ categoryBannerText(category.value) }}</p>
          </div>

          <div class="scan-previews" v-if="scansForCategory(category.value).length">
            <div v-for="scan in scansForCategory(category.value)" :key="scan.id" class="scan-preview">
              <div class="scan-preview__slot">
                <div class="scan-preview__block">
                  <img
                    v-if="isImage(scan.file.filename)"
                    :src="scan.file.path"
                    class="scan-preview__img"
                    @click="toggleBlock($event)"
                  />
                  <div v-else class="scan-preview__pdf">PDF</div>

                  <div class="scan-preview__actions">
                    <a :href="scan.file.path" download class="scan-preview__icon scan-preview__icon--download" @click.stop>
                      <span>Скачать</span>
                      <img src="@images/download.svg" v-svg-inline />
                    </a>
                    <button v-if="canDestroyScan(category.value)" type="button" class="scan-preview__icon scan-preview__icon--remove" @click="destroyScan(scan, category.value)">
                      <span>Удалить</span>
                      <img src="@images/remove-icon.svg" v-svg-inline />
                    </button>
                  </div>
                </div>
              </div>

              <span class="scan-preview__name">{{ scan.file.filename }}</span>
              <span v-if="scan.created_at" class="scan-preview__date">Загружен: {{ scan.created_at }}</span>
            </div>
          </div>

          <div
            v-if="canUploadCategory(category.value)"
            class="scan-category__dropzone"
            :class="{ 'scan-category__dropzone--open': isExpanded(category.value) }"
          >
            <div class="scan-category__dropzone-inner">
              <ScanDropzone @files="uploadFiles($event, category.value)" />
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>

  <Teleport to="body">
    <div class="scan-modal" ref="modalRef">
      <div class="scan-modal__overlay" ref="overlayRef" @click="closeBlock" />
      <div class="scan-modal__content" ref="modalContentRef" />
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useApplicationStore } from '@widgets/application-profile'
import { useScanModal } from './documents/useScanModal'
import ScanDropzone from './documents/ScanDropzone'

const props = defineProps({ readonly: { type: Boolean, default: false } })
const emit = defineEmits(['refetch'])
const store = useApplicationStore()

const expanded = ref({})
const isExpanded = (value) => !!expanded.value[value]
const toggleCategory = (value) => { expanded.value = { ...expanded.value, [value]: !isExpanded(value) } }

const { modalRef, overlayRef, modalContentRef, toggleBlock, closeBlock } = useScanModal()

const isImage = (filename) => /\.(png|jpe?g)$/i.test(filename)

const documentFiles = computed(() => store.application.document_files ?? [])
const scans = computed(() => (store.application.scans ?? []))
const hasDocuments = computed(() => documentFiles.value.length > 0 || scans.value.length > 0)

const scansForCategory = (category) => scans.value.filter(s => s.category === category)
const isFinalStatus = computed(() => Boolean(store.application.in_final_status))
const canUploadCategory = (category) => !props.readonly && (!isFinalStatus.value || category === 'photo' || category === 'profile')
const canDestroyScan = (category) => !props.readonly && (!isFinalStatus.value || category === 'photo' || category === 'profile')

const visibleCategories = computed(() => {
  const categories = store.scanCategories ?? []
  if (props.readonly) return categories.filter(c => scansForCategory(c.value).length > 0)
  return categories
})

const categoryBannerText = (category = '') => {
  if (category === 'photo') return 'Формат 3х4 в классической одежде, формат «фото на документы»'
  if (category === 'identity') return 'Предоставляются сведения о документе, если этот документ, по которому сдавался ЕГЭ, отличается от документа, который указан в заявлении о приеме'
  return ''
}

const uploadFiles = async (files, category) => {
  if (!canUploadCategory(category)) return

  for (const file of files) {
    await store.uploadScan(file, category)
  }
  emit('refetch')
}

const destroyScan = async (scan, category) => {
  if (!canDestroyScan(category)) return

  await store.destroyScan(scan.id)
  emit('refetch')
}
</script>
